import { ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";

import { firmwareWorkflowTone, type FirmwareBetaCustomer, type FirmwareWorkflowState } from "./firmwareApi";
import {
  FIRMWARE_WORKFLOW_HAPPY_PATH,
  FIRMWARE_WORKFLOW_SIDE_CHIPS,
  firmwareAtBetaSiteHoverHint,
  firmwareHappyPathState,
} from "./firmwareWorkflowStageFlow";

export function FirmwareWorkflowStageChips({
  current,
  sentToBeta,
  betaCustomers = [],
  internalOnly = false,
}: {
  current: FirmwareWorkflowState;
  sentToBeta: boolean;
  betaCustomers?: FirmwareBetaCustomer[];
  internalOnly?: boolean;
}): JSX.Element {
  if (internalOnly) {
    return (
      <div className="flex flex-wrap items-center gap-y-2" aria-label="Firmware workflow stages">
        <Badge tone="info" className="text-[11px] ring-2 ring-offset-1 ring-slate-400">
          Internal only
        </Badge>
      </div>
    );
  }

  const flowCurrent = firmwareHappyPathState(current);

  return (
    <div className="flex flex-wrap items-center gap-y-2" aria-label="Firmware workflow stages">
      <ol className="flex flex-wrap items-center">
        {FIRMWARE_WORKFLOW_HAPPY_PATH.map((stage, index) => {
          const lit = flowCurrent === stage;
          return (
            <li key={stage} className="flex items-center" aria-current={lit ? "step" : undefined}>
              {index > 0 ? <ChevronRight className="mx-1 h-4 w-4 shrink-0 text-slate-300" aria-hidden /> : null}
              <Badge
                tone={lit ? firmwareWorkflowTone(stage) : "muted"}
                className={cn("text-[11px]", lit ? "ring-2 ring-offset-1 ring-slate-400" : "opacity-55")}
              >
                {stage}
              </Badge>
            </li>
          );
        })}
      </ol>
      <span className="mx-2 h-4 w-px shrink-0 bg-slate-200" aria-hidden />
      <ul className="flex flex-wrap items-center gap-1.5">
        {FIRMWARE_WORKFLOW_SIDE_CHIPS.map((stage) => {
          const lit =
            stage === "At Beta Site" ? sentToBeta || current === "At Beta Site" : current === stage;
          const badge = (
            <Badge
              tone={lit ? firmwareWorkflowTone(stage) : "muted"}
              className={cn("text-[11px]", lit ? "ring-2 ring-offset-1 ring-slate-400" : "opacity-55")}
            >
              {stage}
            </Badge>
          );
          const betaHoverHint =
            stage === "At Beta Site"
              ? firmwareAtBetaSiteHoverHint(
                  lit,
                  betaCustomers.map((customer) => customer.name),
                )
              : null;
          return (
            <li key={stage} aria-current={lit && flowCurrent == null ? "step" : undefined}>
              {betaHoverHint != null ? (
                <HoverHintContent hoverHint={betaHoverHint}>{badge}</HoverHintContent>
              ) : (
                badge
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
